import { Pencil, RotateCcw } from "lucide-react";
import { useRef, useState, type FocusEvent, type JSX, type KeyboardEvent } from "react";

import { cn } from "@/shared/lib/cn";

interface InlineEditCellProps {
  value: string;
  displayValue?: string;
  /**
   * Commit callback. `remark` is only passed when the optional remark affordance
   * is enabled (`enableRemark`); it is the trimmed operator note or `undefined`
   * when left blank.
   */
  onSave: (newValue: string, remark?: string) => void;
  validate?: (value: string) => string | null;
  className?: string;
  "data-testid"?: string;
  /**
   * Per-field "↺ Default" affordance (PRD v1.8 §4.6.3.3). When provided, a small
   * reset button appears in edit mode. Clicking it sets the local draft to
   * `defaultValue` WITHOUT firing `onSave` — the change only persists via the
   * normal inline Save (Enter / blur).
   */
  defaultValue?: string;
  /** Accessible label for the per-field default affordance. */
  defaultLabel?: string;
  /**
   * Optional remark affordance (PRD v1.8 §4.2). Opt-in — when true, a small
   * optional single-line remark input appears below the value input in edit
   * mode and its trimmed value is passed as the second `onSave` argument. Left
   * blank → `undefined`. Off by default so existing call sites are unchanged.
   */
  enableRemark?: boolean;
  /** Accessible label for the optional remark input. */
  remarkLabel?: string;
  /** Placeholder for the optional remark input. */
  remarkPlaceholder?: string;
  /**
   * Render the value with no edit affordance (no click-to-edit, no ↺ default,
   * no remark). Used when the viewer lacks the write permission — the value is
   * still information they may read.
   */
  readOnly?: boolean;
}

export function InlineEditCell({
  value,
  displayValue,
  onSave,
  validate,
  className,
  "data-testid": testId,
  defaultValue,
  defaultLabel,
  enableRemark = false,
  remarkLabel,
  remarkPlaceholder,
  readOnly = false,
}: InlineEditCellProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [remark, setRemark] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const remarkRef = useRef<HTMLInputElement>(null);
  // Guards against a second commit firing on blur after Enter/Escape already
  // finished the edit (Enter commits → input unmounts → browser fires blur).
  const finishedRef = useRef(false);

  function startEditing() {
    finishedRef.current = false;
    setInputValue(value);
    setRemark("");
    setError(null);
    setIsEditing(true);
    requestAnimationFrame(() => {
      try {
        inputRef.current?.focus();
        inputRef.current?.select();
      } catch {
        // ignore — not critical for functionality
      }
    });
  }

  function commit() {
    if (finishedRef.current) return;
    const validationError = validate?.(inputValue) ?? null;
    if (validationError) {
      setError(validationError);
      return;
    }
    finishedRef.current = true;
    if (enableRemark) {
      const trimmedRemark = remark.trim();
      onSave(inputValue, trimmedRemark ? trimmedRemark : undefined);
    } else {
      // Preserve the single-argument contract for callers that don't opt in.
      onSave(inputValue);
    }
    setIsEditing(false);
    setError(null);
  }

  function cancel() {
    finishedRef.current = true;
    setIsEditing(false);
    setInputValue(value);
    setRemark("");
    setError(null);
  }

  // Blur on the value input must NOT commit when focus is moving to the optional
  // remark input — otherwise the edit would finish before the operator can type
  // the note. Any other blur (clicking away) commits as usual.
  function handleValueBlur(e: FocusEvent<HTMLInputElement>) {
    if (enableRemark && e.relatedTarget === remarkRef.current) return;
    commit();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  }

  // Sets the local draft to the row default WITHOUT committing. Persists only
  // through the normal Save (Enter / blur). Keeps focus on the input so the
  // user can review the value before saving.
  function resetToDefault() {
    if (defaultValue === undefined) return;
    setInputValue(defaultValue);
    setError(null);
    inputRef.current?.focus();
  }

  if (readOnly) {
    // Mirrors the display-state button's box (same padding, width, alignment)
    // minus the pencil, so column widths do not shift between roles.
    return (
      <span
        className={cn(
          "num -mx-[7px] inline-flex w-full items-center justify-end gap-[7px] px-[7px] py-1",
          className,
        )}
        data-testid={testId}
        data-readonly="true"
      >
        <span>{displayValue ?? value}</span>
      </span>
    );
  }

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={startEditing}
        className={cn(
          "num group -mx-[7px] inline-flex w-full items-center justify-end gap-[7px] rounded-[7px] px-[7px] py-1 transition-colors hover:bg-[var(--brand-50)]",
          className,
        )}
        data-testid={testId}
      >
        <span>{displayValue ?? value}</span>
        <Pencil
          className="h-3 w-3 text-[var(--text-subtle)] opacity-40 transition group-hover:text-[var(--brand-500)] group-hover:opacity-100"
          aria-hidden
        />
      </button>
    );
  }

  return (
    <div className="inline-flex flex-col items-end gap-1">
      <div className="inline-flex items-center gap-1.5">
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleValueBlur}
          className={cn(
            "num h-[30px] w-[70px] rounded-[8px] border bg-white px-[9px] text-right text-[13px] outline-none",
            error ? "border-[var(--intent-danger-500)]" : "border-[var(--brand-500)]",
          )}
          style={{
            boxShadow: error ? "0 0 0 3px rgba(208,58,36,.18)" : "0 0 0 3px rgba(15,98,254,.16)",
          }}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        {defaultValue !== undefined && (
          <button
            type="button"
            // Prevent the input's blur (which would commit the current draft)
            // from firing before the draft is reset to the default.
            onMouseDown={(e) => e.preventDefault()}
            onClick={resetToDefault}
            aria-label={defaultLabel}
            title={defaultLabel}
            className="grid h-[30px] w-[26px] shrink-0 place-items-center rounded-[8px] border border-[var(--brand-500)] bg-white text-[var(--text-subtle)] transition hover:bg-[var(--brand-50)] hover:text-[var(--brand-500)] focus-ring"
            data-testid={testId ? `${testId}-default` : undefined}
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>
      {enableRemark && (
        <input
          ref={remarkRef}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          aria-label={remarkLabel}
          placeholder={remarkPlaceholder}
          maxLength={500}
          className="h-[28px] w-[150px] rounded-[8px] border border-[var(--premium-border-strong)] bg-white px-[9px] text-left text-[12px] outline-none focus:border-[var(--brand-500)]"
          data-testid={testId ? `${testId}-remark` : undefined}
        />
      )}
      {error && (
        <span className="text-xs text-[var(--intent-danger-fg)]" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
